import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { isOwnerRole, isAdmin } from '../utils/adminConfig';
import { Home, Shield, Users, TrendingUp, BarChart3, CreditCard, Settings, Search, ChevronDown, ChevronUp, ChevronRight, Crown, UserCheck, UserX, RefreshCw, Activity, Eye, MessageSquare, Trash2, AlertTriangle, Mail, Copy, Check, ArrowUpDown, Archive, Download, Star, CornerUpLeft, ArrowLeft, RotateCw, MousePointerClick, Link2 } from 'lucide-react';
import { exportAnalyticsCsv } from '../utils/analyticsExport';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const COLORS = ['#8b5cf6', '#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

// Sanitize HTML for safe preview — strips scripts, event handlers, and dangerous tags
const sanitizeHtml = (html) => {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/<object[\s\S]*?<\/object>/gi, '')
    .replace(/<embed[\s\S]*?>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*\S+/gi, '')
    .replace(/javascript\s*:/gi, '');
};

// Renders a small icon next to a page_view event line in the activity timeline
// to show HOW the user navigated to that page. Captures four nav classes:
//   • back arrow click  — explicit in-app back arrow (navSource includes
//                         'back_arrow'). The single highest-signal indicator
//                         since it tells us the user "rejected" the page.
//   • browser back/fwd  — popstate (navAction='POP' && !isInitialLoad).
//                         Differs from a back arrow because the user used
//                         the browser/system back, not a UI element.
//   • redirect          — navigate(..., { replace: true }) or <Navigate replace />.
//   • in-app click      — any other PUSH navigation (we have an explicit
//                         navSource label, so we show the click icon).
// Cold ad lands (POP + isInitialLoad) get NO badge — they're the default and
// adding an icon would just add visual noise.
const NavBadge = ({ event }) => {
  if (event.type !== 'page_view' || !event.metadata) return null;
  const m = event.metadata;
  const src = m.navSource || '';

  // Most specific signal first: an explicit in-app back arrow click.
  if (src.includes('back_arrow')) {
    return (
      <span title={`Back arrow click · navSource=${src}`} className="text-orange-500 flex-shrink-0">
        <CornerUpLeft size={12} />
      </span>
    );
  }
  // Browser back/forward (popstate) — non-initial POP navigation.
  if (m.navAction === 'POP' && m.isInitialLoad === false) {
    return (
      <span title="Browser back/forward (popstate)" className="text-amber-500 flex-shrink-0">
        <ArrowLeft size={12} />
      </span>
    );
  }
  // Programmatic redirect — navigate({ replace }) or <Navigate replace />.
  // Exclude initial page loads: React Router v7 reports 'REPLACE' for direct
  // URL entries in BrowserRouter compatibility mode, so without this guard
  // every cold-land page view would show the purple redirect icon.
  if (m.navAction === 'REPLACE' && m.isInitialLoad === false) {
    return (
      <span title={`Redirect (replace)${src ? ` · navSource=${src}` : ''}`} className="text-purple-500 flex-shrink-0">
        <RotateCw size={12} />
      </span>
    );
  }
  // In-app click navigation (PUSH) with an explicit source label.
  if (m.navAction === 'PUSH' && src) {
    return (
      <span title={`In-app click · navSource=${src}`} className="text-blue-500 flex-shrink-0">
        <MousePointerClick size={12} />
      </span>
    );
  }
  return null;
};

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, userData, getAllUsers, getAnalyticsEvents, archiveAnalytics, clearAnalytics, getArchivedAnalytics, getAdminLogs, getAllFeedback, deleteFeedback, getAllReviews, deleteReview, getContactSubmissions, deleteContactSubmission, promoteToAdmin, revokeAdmin, grantPaidAccess, revokePaidAccess, deleteUser, assignReferralCode, removeReferralCode } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [allUsers, setAllUsers] = useState([]);
  const [analyticsEvents, setAnalyticsEvents] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [promoteEmail, setPromoteEmail] = useState('');
  const [actionMessage, setActionMessage] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [allFeedback, setAllFeedback] = useState([]);
  const [trafficTimeRange, setTrafficTimeRange] = useState('all');
  const [excludeDirect, setExcludeDirect] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [copiedEmails, setCopiedEmails] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailAudience, setEmailAudience] = useState('emailOptIn');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [archives, setArchives] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [viewingArchive, setViewingArchive] = useState(null);
  const [feedbackDeleteConfirm, setFeedbackDeleteConfirm] = useState(null);
  const [feedbackDeleting, setFeedbackDeleting] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [contactDeleteConfirm, setContactDeleteConfirm] = useState(null);
  const [contactDeleting, setContactDeleting] = useState(false);
  const [allReviews, setAllReviews] = useState([]);
  const [reviewDeleteConfirm, setReviewDeleteConfirm] = useState(null);
  const [reviewDeleting, setReviewDeleting] = useState(false);
  const [referralSearch, setReferralSearch] = useState('');
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [referralAssignUid, setReferralAssignUid] = useState('');
  const [referralActionMsg, setReferralActionMsg] = useState(null);
  const [referralAssigning, setReferralAssigning] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleDeleteFeedback = async (feedbackId) => {
    setFeedbackDeleting(true);
    const result = await deleteFeedback(feedbackId);
    if (result.success) {
      setAllFeedback(prev => prev.filter(f => f.id !== feedbackId));
      setActionMessage({ type: 'success', text: 'Feedback deleted' });
    } else {
      setActionMessage({ type: 'error', text: 'Failed to delete feedback' });
    }
    setFeedbackDeleteConfirm(null);
    setFeedbackDeleting(false);
    setTimeout(() => setActionMessage(null), 3000);
  };

  const handleDeleteContact = async (contactId) => {
    setContactDeleting(true);
    const result = await deleteContactSubmission(contactId);
    if (result.success) {
      setContactSubmissions(prev => prev.filter(c => c.id !== contactId));
      setActionMessage({ type: 'success', text: 'Contact submission deleted' });
    } else {
      setActionMessage({ type: 'error', text: 'Failed to delete contact submission' });
    }
    setContactDeleteConfirm(null);
    setContactDeleting(false);
    setTimeout(() => setActionMessage(null), 3000);
  };

  const handleDeleteReview = async (reviewId) => {
    setReviewDeleting(true);
    const result = await deleteReview(reviewId);
    if (result.success) {
      setAllReviews(prev => prev.filter(r => r.id !== reviewId));
      setActionMessage({ type: 'success', text: 'Review deleted' });
    } else {
      setActionMessage({ type: 'error', text: 'Failed to delete review' });
    }
    setReviewDeleteConfirm(null);
    setReviewDeleting(false);
    setTimeout(() => setActionMessage(null), 3000);
  };

  const handleAssignReferralCode = async () => {
    if (!referralAssignUid || !referralCodeInput.trim()) {
      setReferralActionMsg({ type: 'error', text: 'Select a user and enter a code' });
      setTimeout(() => setReferralActionMsg(null), 3000);
      return;
    }
    setReferralAssigning(true);
    const result = await assignReferralCode(referralAssignUid, referralCodeInput.trim());
    if (result.success) {
      setReferralActionMsg({ type: 'success', text: 'Referral code assigned!' });
      setReferralCodeInput('');
      setReferralAssignUid('');
      setReferralSearch('');
      const users = await getAllUsers();
      setAllUsers(users);
    } else {
      setReferralActionMsg({ type: 'error', text: result.error });
    }
    setReferralAssigning(false);
    setTimeout(() => setReferralActionMsg(null), 3000);
  };

  const handleRemoveReferralCode = async (uid) => {
    const result = await removeReferralCode(uid);
    if (result.success) {
      setReferralActionMsg({ type: 'success', text: 'Code removed' });
      const users = await getAllUsers();
      setAllUsers(users);
    } else {
      setReferralActionMsg({ type: 'error', text: result.error });
    }
    setTimeout(() => setReferralActionMsg(null), 3000);
  };

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [users, events, logs, feedback, archivedData, contacts, reviews] = await Promise.all([
        getAllUsers(),
        getAnalyticsEvents(1000),
        getAdminLogs(),
        getAllFeedback(),
        getArchivedAnalytics(),
        getContactSubmissions(),
        getAllReviews()
      ]);
      setAllUsers(users);
      setAnalyticsEvents(events);
      setAdminLogs(logs);
      setAllFeedback(feedback);
      setArchives(archivedData);
      setContactSubmissions(contacts);
      setAllReviews(reviews);
    } catch (err) {
      console.error('Error loading admin data:', err);
    }
    setLoadingData(false);
  }, [getAllUsers, getAnalyticsEvents, getAdminLogs, getAllFeedback, getArchivedAnalytics, getAllReviews]);

  useEffect(() => { loadData(); }, [loadData]);

  // Computed stats — exclude owner/admin accounts from all counts so the
  // dashboard reflects real users only. allUsers is kept for the Users tab.
  const now = new Date();
  const realUsers = allUsers.filter(u => !isAdmin(u.role));
  const totalUsers = realUsers.length;
  const paidUsers = realUsers.filter(u => u.subscription === 'paid').length;
  const freeUsers = totalUsers - paidUsers;
  const activeUsers7d = realUsers.filter(u => {
    const lv = u.lastVisit ? (typeof u.lastVisit === 'number' ? u.lastVisit : new Date(u.lastVisit).getTime()) : 0;
    const ll = u.lastLessonDate ? new Date(u.lastLessonDate).getTime() : 0;
    const latest = Math.max(lv, ll);
    if (!latest) return false;
    return (now.getTime() - latest) < 7 * 24 * 60 * 60 * 1000;
  }).length;

  const todayStr = now.toISOString().split('T')[0];
  const signupsToday = realUsers.filter(u => u.createdAt && u.createdAt.startsWith(todayStr)).length;
  const signupsThisWeek = realUsers.filter(u => {
    if (!u.createdAt) return false;
    return (now.getTime() - new Date(u.createdAt).getTime()) < 7 * 24 * 60 * 60 * 1000;
  }).length;
  const signupsThisMonth = realUsers.filter(u => {
    if (!u.createdAt) return false;
    return (now.getTime() - new Date(u.createdAt).getTime()) < 30 * 24 * 60 * 60 * 1000;
  }).length;

  const totalLessonsCompleted = realUsers.reduce((sum, u) => {
    if (u.phaseProgress) {
      return sum + Object.values(u.phaseProgress).reduce((pSum, p) => pSum + (p.completedLessons?.length || 0), 0);
    }
    return sum + (u.completedLessons?.length || 0);
  }, 0);

  const monthlySubscribers = realUsers.filter(u => u.planType === 'monthly' && u.subscription === 'paid').length;
  const annualSubscribers = realUsers.filter(u => u.planType === 'annual' && u.subscription === 'paid').length;

  // Signup trend (last 30 days)
  const signupTrend = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const count = realUsers.filter(u => u.createdAt && u.createdAt.startsWith(dateStr)).length;
    signupTrend.push({ date: `${d.getMonth() + 1}/${d.getDate()}`, signups: count });
  }

  // Phase distribution
  const phaseDistribution = [1, 2, 3, 4].map(p => ({
    name: `Phase ${p}`,
    value: realUsers.filter(u => u.currentPhase === p).length
  }));

  // Streak distribution
  const streakBuckets = [
    { name: '0 days', value: realUsers.filter(u => !u.currentStreak || u.currentStreak === 0).length },
    { name: '1-3 days', value: realUsers.filter(u => u.currentStreak >= 1 && u.currentStreak <= 3).length },
    { name: '4-7 days', value: realUsers.filter(u => u.currentStreak >= 4 && u.currentStreak <= 7).length },
    { name: '8-14 days', value: realUsers.filter(u => u.currentStreak >= 8 && u.currentStreak <= 14).length },
    { name: '15+ days', value: realUsers.filter(u => u.currentStreak >= 15).length },
  ];

  // Subscription pie
  const subscriptionPie = [
    { name: 'Free', value: freeUsers },
    { name: 'Monthly', value: monthlySubscribers },
    { name: 'Annual', value: annualSubscribers },
  ].filter(s => s.value > 0);

  // Filter events by time range
  const filterEventsByTimeRange = (events, range) => {
    if (range === 'all') return events;
    const cutoffs = { '24h': 1, '7d': 7, '30d': 30, '90d': 90 };
    const days = cutoffs[range];
    if (!days) return events;
    const cutoff = now.getTime() - days * 24 * 60 * 60 * 1000;
    return events.filter(e => {
      if (!e.timestamp) return false;
      const ts = typeof e.timestamp === 'number' ? e.timestamp
        : e.timestamp?.toDate ? e.timestamp.toDate().getTime()
        : new Date(e.timestamp).getTime();
      return ts >= cutoff;
    });
  };

  // Build set of admin emails for traffic filtering
  const adminEmails = new Set(
    allUsers.filter(u => u.role === 'admin' || u.role === 'owner').map(u => u.email?.toLowerCase())
  );
  const filteredTrafficEvents = filterEventsByTimeRange(analyticsEvents, trafficTimeRange)
    .filter(e => !adminEmails.has(e.userId?.toLowerCase()))
    .filter(e => {
      if (!excludeDirect) return true;
      const src = (e.referralSource || 'direct').toLowerCase();
      return src !== 'direct' && src !== '';
    });

  // Page view stats from analytics (time-filtered).
  // Each screen owns exactly one event tag now — no normalization needed.
  //   home                   = marketing landing
  //   free-activity-intro    = cold-lander intro screen
  //   free-activity          = age picker
  //   free-activity-lesson   = the activity itself (anonymous trial)
  //   lesson                 = the activity itself (logged-in users)
  const pageViewCounts = {};
  filteredTrafficEvents.filter(e => e.type === 'page_view').forEach(e => {
    const page = e.page || 'unknown';
    pageViewCounts[page] = (pageViewCounts[page] || 0) + 1;
  });
  const pageViewData = Object.entries(pageViewCounts).map(([page, count]) => ({ page, views: count })).sort((a, b) => b.views - a.views);

  // Referral source breakdown (time-filtered, normalized, localhost removed, counted by unique session)
  const referralCounts = {};
  const referralSessionsSeen = new Set();
  filteredTrafficEvents.forEach(e => {
    const src = (e.referralSource || 'direct').toLowerCase();
    if (src === 'localhost' || src === '127.0.0.1' || src === '') return;
    const sessionKey = `${e.sessionId}_${src}`;
    if (referralSessionsSeen.has(sessionKey)) return;
    referralSessionsSeen.add(sessionKey);
    referralCounts[src] = (referralCounts[src] || 0) + 1;
  });
  const referralData = Object.entries(referralCounts)
    .map(([source, count]) => ({ source, sessions: count }))
    .sort((a, b) => b.sessions - a.sessions);

  // Recent events
  const recentEvents = analyticsEvents.slice(0, 200);

  // Subscription events from analytics
  const subscriptionEvents = analyticsEvents.filter(e => ['subscription_start', 'subscription_cancel'].includes(e.type));

  // Lesson completion events
  const lessonEvents = analyticsEvents.filter(e => ['lesson_start', 'lesson_complete'].includes(e.type));

  // Filtered users
  const filteredUsers = allUsers
    .filter(u => {
      if (userFilter === 'paid') return u.subscription === 'paid';
      if (userFilter === 'free') return u.subscription === 'free';
      if (userFilter === 'admin') return u.role === 'admin' || u.role === 'owner';
      if (userFilter === 'emailOptIn') return u.emailOptIn === true;
      return true;
    })
    .filter(u => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (u.username || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term) ||
        (u.parentName || '').toLowerCase().includes(term) ||
        (u.childName || (u.children || []).map(c => c.name).filter(Boolean).join(' ') || '').toLowerCase().includes(term);
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const getLessons = (u) => u.phaseProgress ? Object.values(u.phaseProgress).reduce((s, p) => s + (p.completedLessons?.length || 0), 0) : (u.completedLessons?.length || 0);
      switch (sortField) {
        case 'createdAt': return dir * ((a.createdAt ? new Date(a.createdAt).getTime() : 0) - (b.createdAt ? new Date(b.createdAt).getTime() : 0));
        case 'lastVisit': return dir * ((a.lastVisit || 0) - (b.lastVisit || 0));
        case 'lastLesson': {
          const aDate = a.lastLessonDate ? new Date(a.lastLessonDate).getTime() : 0;
          const bDate = b.lastLessonDate ? new Date(b.lastLessonDate).getTime() : 0;
          return dir * (aDate - bDate);
        }
        case 'streak': return dir * ((a.currentStreak || 0) - (b.currentStreak || 0));
        case 'lessons': return dir * (getLessons(a) - getLessons(b));
        case 'phase': return dir * ((a.currentPhase || 1) - (b.currentPhase || 1));
        case 'username': return dir * (a.username || '').localeCompare(b.username || '');
        case 'email': return dir * (a.email || '').localeCompare(b.email || '');
        default: return 0;
      }
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleCopyEmails = () => {
    const emails = filteredUsers.filter(u => u.email).map(u => u.email).join('\n');
    navigator.clipboard.writeText(emails);
    setCopiedEmails(true);
    setTimeout(() => setCopiedEmails(false), 2000);
  };

  const handlePromote = async () => {
    if (!promoteEmail.trim()) return;
    const targetUser = allUsers.find(u => u.email?.toLowerCase() === promoteEmail.toLowerCase().trim());
    if (!targetUser) { setActionMessage({ type: 'error', text: 'User not found' }); return; }
    if (targetUser.role === 'owner') { setActionMessage({ type: 'error', text: 'Cannot modify owner' }); return; }
    if (targetUser.role === 'admin') { setActionMessage({ type: 'error', text: 'Already an admin' }); return; }
    const result = await promoteToAdmin(targetUser.uid);
    if (result.success) {
      setActionMessage({ type: 'success', text: `${targetUser.email} promoted to admin` });
      setPromoteEmail('');
      loadData();
    } else {
      setActionMessage({ type: 'error', text: result.error });
    }
  };

  const handleRevoke = async (targetUid, email) => {
    const result = await revokeAdmin(targetUid);
    if (result.success) {
      setActionMessage({ type: 'success', text: `Admin access revoked for ${email}` });
      loadData();
    } else {
      setActionMessage({ type: 'error', text: result.error });
    }
  };

  const handleGrantPaid = async (targetUid, email) => {
    const result = await grantPaidAccess(targetUid);
    if (result.success) {
      setActionMessage({ type: 'success', text: `Paid access granted to ${email}` });
      loadData();
    } else {
      setActionMessage({ type: 'error', text: result.error });
    }
  };

  const handleRevokePaid = async (targetUid, email) => {
    const result = await revokePaidAccess(targetUid);
    if (result.success) {
      setActionMessage({ type: 'success', text: `Paid access revoked for ${email}` });
      loadData();
    } else {
      setActionMessage({ type: 'error', text: result.error });
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;
    setDeleteLoading(true);
    const result = await deleteUser(deleteConfirm.uid);
    if (result.success) {
      setActionMessage({ type: 'success', text: `User ${deleteConfirm.username || deleteConfirm.email} has been permanently deleted` });
      setDeleteConfirm(null);
      loadData();
    } else {
      setActionMessage({ type: 'error', text: result.error || 'Failed to delete user' });
      setDeleteConfirm(null);
    }
    setDeleteLoading(false);
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return '—';
    try {
      const d = dateVal.toDate ? dateVal.toDate() : new Date(typeof dateVal === 'number' ? dateVal : dateVal);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return '—'; }
  };

  const formatTimeAgo = (dateVal) => {
    if (!dateVal) return '—';
    try {
      const d = dateVal.toDate ? dateVal.toDate() : new Date(typeof dateVal === 'number' ? dateVal : dateVal);
      const diff = now.getTime() - d.getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      return `${days}d ago`;
    } catch { return '—'; }
  };

  // Group recent events by sessionId, then sort groups by most recent event.
  // Within each group, events are sorted oldest-first so the feed reads like
  // a timeline: "user landed on home → scrolled → clicked CTA → signed up".
  const SESSION_PALETTE = [
    '#8b5cf6', '#ec4899', '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#e879f9',
    '#facc15', '#fb7185', '#22d3ee', '#a3e635',
  ];
  const { sessionGroups, sessionColorMap } = React.useMemo(() => {
    const grouped = {};
    (recentEvents || []).forEach(e => {
      const sid = e.sessionId || 'unknown';
      if (!grouped[sid]) grouped[sid] = [];
      grouped[sid].push(e);
    });

    // Merge sessions that are almost certainly the same user — same device type,
    // same referral source, and first events within 2 minutes of each other.
    // This catches duplicate sessions from in-app browser re-initializations
    // (Facebook/Instagram WebViews) that create new session IDs on page reload.
    const sids = Object.keys(grouped);
    const getTs = (e) => { try { return e?.timestamp?.toDate?.()?.getTime() || (typeof e?.timestamp === 'number' ? e.timestamp : 0); } catch { return 0; } };
    const merged = new Map(); // canonical sid -> merged events
    const sidToCanonical = {}; // maps each sid to its canonical sid
    const MERGE_WINDOW_MS = 2 * 60 * 1000;

    sids.forEach(sid => {
      const events = grouped[sid];
      const first = events[events.length - 1] || events[0]; // oldest event (recentEvents is desc)
      const fingerprint = `${first?.deviceType || ''}_${first?.referralSource || ''}`;
      const firstTs = getTs(first);

      // Check if this session should merge into an existing canonical session
      let mergedInto = null;
      for (const [canonSid, canonEvents] of merged) {
        const canonFirst = canonEvents[canonEvents.length - 1] || canonEvents[0];
        const canonFp = `${canonFirst?.deviceType || ''}_${canonFirst?.referralSource || ''}`;
        const canonTs = getTs(canonFirst);
        if (fingerprint === canonFp && Math.abs(firstTs - canonTs) < MERGE_WINDOW_MS) {
          mergedInto = canonSid;
          break;
        }
      }

      if (mergedInto) {
        merged.get(mergedInto).push(...events);
        sidToCanonical[sid] = mergedInto;
      } else {
        merged.set(sid, [...events]);
        sidToCanonical[sid] = sid;
      }
    });

    // Deduplicate events within merged groups (same type + same key metadata)
    for (const [, events] of merged) {
      const seen = new Set();
      for (let i = events.length - 1; i >= 0; i--) {
        const e = events[i];
        const dedupKey = `${e.type}_${e.page || ''}_${e.metadata?.section || ''}_${e.metadata?.depth || ''}`;
        if (seen.has(dedupKey)) {
          events.splice(i, 1);
        } else {
          seen.add(dedupKey);
        }
      }
    }

    // Sort groups by most recent event (first event in each group, since events are desc)
    let sortedKeys = [...merged.keys()].sort((a, b) => {
      const aTs = getTs(merged.get(a)[0]);
      const bTs = getTs(merged.get(b)[0]);
      return bTs - aTs;
    });
    // Drop direct/tagless sessions when the toggle is on. A session counts as
    // direct/tagless if NONE of its events carry a real referralSource.
    if (excludeDirect) {
      sortedKeys = sortedKeys.filter(sid => {
        const events = merged.get(sid) || [];
        return events.some(e => {
          const raw = e.referralSource;
          if (raw == null) return false;
          const src = String(raw).trim().toLowerCase();
          return src !== '' && src !== 'direct' && src !== 'unknown' && src !== 'none';
        });
      });
    }
    // Cap to 50 sessions for the feed
    sortedKeys = sortedKeys.slice(0, 50);
    // Reverse events within each group so oldest is first (timeline order)
    const groups = sortedKeys.map(sid => ({ sessionId: sid, events: [...merged.get(sid)].reverse() }));
    // Assign colors in display order
    const colorMap = {};
    sortedKeys.forEach((sid, i) => { colorMap[sid] = SESSION_PALETTE[i % SESSION_PALETTE.length]; });
    return { sessionGroups: groups, sessionColorMap: colorMap };
  }, [recentEvents, excludeDirect]);
  const getSessionLabel = (sessionId) => {
    if (!sessionId) return '?';
    const idx = sessionGroups.findIndex(g => g.sessionId === sessionId);
    return idx >= 0 ? `S${idx + 1}` : '?';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <BarChart3 size={18} /> },
    { id: 'users', label: 'Users', icon: <Users size={18} /> },
    { id: 'engagement', label: 'Engagement', icon: <TrendingUp size={18} /> },
    { id: 'traffic', label: 'Traffic', icon: <Activity size={18} /> },
    { id: 'subscriptions', label: 'Subscriptions', icon: <CreditCard size={18} /> },
    { id: 'referrals', label: 'Referrals', icon: <Link2 size={18} /> },
    { id: 'archives', label: 'Archives', icon: <Archive size={18} /> },
    { id: 'feedback', label: 'Feedback', icon: <MessageSquare size={18} /> },
    { id: 'email', label: 'Email', icon: <Mail size={18} /> },
    ...(isOwnerRole(userData?.role) ? [{ id: 'admin', label: 'Admin Mgmt', icon: <Settings size={18} /> }] : [])
  ];

  const StatCard = ({ label, value, sub, color = 'purple' }) => (
    <div className="bg-white rounded-xl shadow-md p-5">
      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-black text-${color}-600 mt-1`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className={`min-h-screen min-h-[100dvh] bg-gradient-to-b from-slate-50 via-blue-50 to-indigo-50`}>
      {/* Nav */}
      <nav className="bg-white/80 shadow-sm p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="text-purple-600" size={28} />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${userData?.role === 'owner' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
              {userData?.role === 'owner' ? '👑 Owner' : '🛡️ Admin'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadData} className="text-gray-500 hover:text-purple-600 p-2 rounded-lg hover:bg-purple-50 transition" title="Refresh data">
              <RefreshCw size={20} className={loadingData ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => handleNavigate('/dashboard')} className="text-purple-600 hover:bg-purple-50 px-4 py-2 rounded-lg transition font-medium">
              Dashboard
            </button>
            <button onClick={() => handleNavigate('/')} className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition">
              <Home size={24} />
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition ${activeTab === tab.id ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-50 shadow'}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {actionMessage && activeTab !== 'admin' && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-semibold ${actionMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {actionMessage.text}
          </div>
        )}

        {loadingData && (
          <div className="text-center py-12">
            <RefreshCw size={32} className="animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading admin data...</p>
          </div>
        )}

        {!loadingData && (
          <>
            {/* ═══ OVERVIEW TAB ═══ */}
            {activeTab === 'overview' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard label="Total Users" value={totalUsers} sub={`+${signupsThisWeek} this week`} />
                  <StatCard label="Active (7d)" value={activeUsers7d} sub={`${totalUsers ? Math.round(activeUsers7d / totalUsers * 100) : 0}% of total`} color="blue" />
                  <StatCard label="Paid Users" value={paidUsers} sub={`${totalUsers ? Math.round(paidUsers / totalUsers * 100) : 0}% conversion`} color="green" />
                  <StatCard label="Lessons Done" value={totalLessonsCompleted} sub={`${totalUsers ? (totalLessonsCompleted / totalUsers).toFixed(1) : 0} avg/user`} color="pink" />
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <StatCard label="Signups Today" value={signupsToday} color="blue" />
                  <StatCard label="This Week" value={signupsThisWeek} color="purple" />
                  <StatCard label="This Month" value={signupsThisMonth} color="pink" />
                </div>

                {/* Signup Trend Chart */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4">Signups — Last 30 Days</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={signupTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="signups" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Subscription Breakdown */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Subscription Breakdown</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={subscriptionPie} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {subscriptionPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Phase Distribution */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Phase Distribution</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={phaseDistribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ USERS TAB ═══ */}
            {activeTab === 'users' && (
              <div>
                <div className="flex flex-wrap gap-3 mb-4">
                  <div className="flex-1 min-w-0 sm:min-w-[200px] relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input type="text" placeholder="Search by name, email, username..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-400 outline-none text-sm" />
                  </div>
                  <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
                    className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-purple-400 outline-none">
                    <option value="all">All Users</option>
                    <option value="paid">Paid</option>
                    <option value="free">Free</option>
                    <option value="admin">Admins</option>
                    <option value="emailOptIn">Email Friendly</option>
                  </select>
                  <button
                    onClick={handleCopyEmails}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition ${copiedEmails ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200'}`}
                    title={`Copy ${filteredUsers.filter(u => u.email).length} emails to clipboard`}
                  >
                    {copiedEmails ? <Check size={16} /> : <Copy size={16} />}
                    {copiedEmails ? 'Copied!' : `Copy ${filteredUsers.filter(u => u.email).length} Emails`}
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                        <tr>
                          <th className="px-4 py-3 text-left font-bold cursor-pointer hover:bg-white/10 transition" onClick={() => handleSort('username')}>
                            <div className="flex items-center gap-1">User {sortField === 'username' && <ArrowUpDown size={12} />}</div>
                          </th>
                          <th className="px-4 py-3 text-left font-bold">Role</th>
                          <th className="px-4 py-3 text-left font-bold">Plan</th>
                          <th className="px-4 py-3 text-center font-bold" title="Email Opt-In">
                            <Mail size={14} className="mx-auto" />
                          </th>
                          <th className="px-4 py-3 text-left font-bold cursor-pointer hover:bg-white/10 transition" onClick={() => handleSort('phase')}>
                            <div className="flex items-center gap-1">Phase {sortField === 'phase' && <ArrowUpDown size={12} />}</div>
                          </th>
                          <th className="px-4 py-3 text-left font-bold cursor-pointer hover:bg-white/10 transition" onClick={() => handleSort('lessons')}>
                            <div className="flex items-center gap-1">Lessons {sortField === 'lessons' && <ArrowUpDown size={12} />}</div>
                          </th>
                          <th className="px-4 py-3 text-left font-bold cursor-pointer hover:bg-white/10 transition" onClick={() => handleSort('streak')}>
                            <div className="flex items-center gap-1">Streak {sortField === 'streak' && <ArrowUpDown size={12} />}</div>
                          </th>
                          <th className="px-4 py-3 text-left font-bold cursor-pointer hover:bg-white/10 transition" onClick={() => handleSort('lastVisit')}>
                            <div className="flex items-center gap-1">Last Visit {sortField === 'lastVisit' && <ArrowUpDown size={12} />}</div>
                          </th>
                          <th className="px-4 py-3 text-left font-bold cursor-pointer hover:bg-white/10 transition" onClick={() => handleSort('lastLesson')}>
                            <div className="flex items-center gap-1">Last Lesson {sortField === 'lastLesson' && <ArrowUpDown size={12} />}</div>
                          </th>
                          <th className="px-4 py-3 text-left font-bold cursor-pointer hover:bg-white/10 transition" onClick={() => handleSort('createdAt')}>
                            <div className="flex items-center gap-1">Joined {sortField === 'createdAt' && <ArrowUpDown size={12} />}</div>
                          </th>
                          <th className="px-4 py-3 text-center font-bold w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredUsers.map(u => {
                          const lessonsCount = u.phaseProgress
                            ? Object.values(u.phaseProgress).reduce((s, p) => s + (p.completedLessons?.length || 0), 0)
                            : (u.completedLessons?.length || 0);
                          const isExpanded = expandedUserId === u.uid;
                          return (
                            <React.Fragment key={u.uid}>
                            <tr className={`hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-purple-50' : ''}`} onClick={() => setExpandedUserId(isExpanded ? null : u.uid)}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400 transition-transform" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                                    <ChevronRight size={14} />
                                  </span>
                                  <div>
                                    <div className="font-bold text-gray-900">{u.username || '—'}</div>
                                    <div className="text-xs text-gray-400">{u.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.role === 'owner' ? 'bg-yellow-100 text-yellow-700' : u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {u.role || 'user'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.subscription === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {u.subscription === 'paid' ? u.planType || 'paid' : 'free'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {u.emailOptIn && (
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100" title="Opted in to emails">
                                    <Mail size={12} className="text-green-600" />
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 font-semibold text-gray-700">{u.currentPhase || 1}</td>
                              <td className="px-4 py-3 font-semibold text-gray-700">{lessonsCount}</td>
                              <td className="px-4 py-3 font-semibold text-gray-700">{u.currentStreak || 0}🔥</td>
                              <td className="px-4 py-3 text-xs text-gray-500">{formatTimeAgo(u.lastVisit)}</td>
                              <td className="px-4 py-3 text-xs text-gray-500">{formatTimeAgo(u.lastLessonDate)}</td>
                              <td className="px-4 py-3 text-xs text-gray-500">{formatDate(u.createdAt)}</td>
                              <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                                {u.role !== 'owner' && (
                                  <button
                                    onClick={() => setDeleteConfirm(u)}
                                    className="text-gray-400 hover:text-red-600 p-1 rounded-lg hover:bg-red-50 transition"
                                    title={`Delete ${u.username || u.email}`}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="bg-purple-50/50">
                                <td colSpan={11} className="px-6 py-4">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    {/* User Info */}
                                    <div className="bg-white rounded-lg p-4 shadow-sm">
                                      <div className="flex items-center gap-3 mb-2">
                                        <img src={`/assets/${u.profilePicture || 'Bobby Bear.png'}`} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-purple-200 flex-shrink-0" />
                                        <h4 className="font-bold text-gray-900">User Info</h4>
                                      </div>
                                      <div className="space-y-1 text-gray-600">
                                        <div><span className="font-medium">Username:</span> {u.username || '—'}</div>
                                        <div><span className="font-medium">Email:</span> {u.email}</div>
                                        <div><span className="font-medium">Parent:</span> {u.parentName || '—'}</div>
                                        <div><span className="font-medium">Child:</span> {u.childName || (u.children || []).map(c => c.name).filter(Boolean).join(', ') || '—'}</div>
                                        <div><span className="font-medium">Age Group:</span> {u.selectedAge || '—'}</div>
                                        <div><span className="font-medium">Joined:</span> {formatDate(u.createdAt)}</div>
                                        <div><span className="font-medium">Last Visit:</span> {formatTimeAgo(u.lastVisit)}</div>
                                        <div><span className="font-medium">Last Lesson:</span> {formatTimeAgo(u.lastLessonDate)}</div>
                                      </div>
                                    </div>

                                    {/* Phase Progress */}
                                    <div className="bg-white rounded-lg p-4 shadow-sm">
                                      <h4 className="font-bold text-gray-900 mb-2">Phase Progress</h4>
                                      <div className="space-y-2">
                                        {[1, 2, 3, 4].map(phase => {
                                          const phaseData = u.phaseProgress?.[phase] || { completedLessons: [] };
                                          const completed = phaseData.completedLessons?.length || 0;
                                          const isCurrent = (u.currentPhase || 1) === phase;
                                          return (
                                            <div key={phase} className={`flex items-center gap-2 p-2 rounded ${isCurrent ? 'bg-purple-100 border border-purple-200' : 'bg-gray-50'}`}>
                                              <span className={`font-bold text-xs px-2 py-0.5 rounded ${isCurrent ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}>P{phase}</span>
                                              <span className="text-gray-700 font-medium">{completed} lesson{completed !== 1 ? 's' : ''}</span>
                                              {isCurrent && <span className="text-xs text-purple-600 font-medium ml-auto">Current</span>}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* Completed Lessons Detail */}
                                    <div className="bg-white rounded-lg p-4 shadow-sm">
                                      <h4 className="font-bold text-gray-900 mb-2">Recent Lessons</h4>
                                      <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {(() => {
                                          // Gather all lesson completions across phases with dates
                                          const allLessons = [];
                                          if (u.phaseProgress) {
                                            Object.entries(u.phaseProgress).forEach(([phase, data]) => {
                                              if (data?.lessonCompletionDates) {
                                                Object.entries(data.lessonCompletionDates).forEach(([lessonId, date]) => {
                                                  allLessons.push({ phase, lessonId, date });
                                                });
                                              }
                                            });
                                          }
                                          allLessons.sort((a, b) => new Date(b.date) - new Date(a.date));
                                          if (allLessons.length === 0) return <div className="text-gray-400 text-xs">No lessons completed yet</div>;
                                          return allLessons.slice(0, 15).map((l, i) => (
                                            <div key={i} className="flex justify-between text-xs py-1 border-b border-gray-50">
                                              <span className="text-gray-700">P{l.phase} - Lesson {l.lessonId}</span>
                                              <span className="text-gray-400">{formatDate(l.date)}</span>
                                            </div>
                                          ));
                                        })()}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Subscription & Stats Row */}
                                  <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                                      <div className="text-xs text-gray-500">Subscription</div>
                                      <div className={`font-bold ${u.subscription === 'paid' ? 'text-green-600' : 'text-gray-600'}`}>
                                        {u.subscription === 'paid' ? u.planType || 'Paid' : 'Free'}
                                      </div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                                      <div className="text-xs text-gray-500">Current Streak</div>
                                      <div className="font-bold text-orange-600">{u.currentStreak || 0} days</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                                      <div className="text-xs text-gray-500">Total Lessons</div>
                                      <div className="font-bold text-purple-600">{lessonsCount}</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                                      <div className="text-xs text-gray-500">Friends</div>
                                      <div className="font-bold text-blue-600">{u.friends?.length || 0}</div>
                                    </div>
                                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                                      <div className="text-xs text-gray-500">Email Opt-in</div>
                                      <div className={`font-bold ${u.emailOptIn ? 'text-green-600' : 'text-gray-400'}`}>{u.emailOptIn ? 'Yes' : 'No'}</div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-3 bg-gray-50 text-xs text-gray-500 flex justify-between items-center">
                    <span>Showing {filteredUsers.length} of {totalUsers} users</span>
                    <span>{realUsers.filter(u => u.emailOptIn).length} email-friendly users total</span>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ ENGAGEMENT TAB ═══ */}
            {activeTab === 'engagement' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard label="Avg Lessons/User" value={totalUsers ? (totalLessonsCompleted / totalUsers).toFixed(1) : 0} color="purple" />
                  <StatCard label="Game Events" value={analyticsEvents.filter(e => e.type === 'game_start').length} color="blue" />
                  <StatCard label="Lesson Starts" value={lessonEvents.filter(e => e.type === 'lesson_start').length} color="green" />
                  <StatCard label="Lesson Completions" value={lessonEvents.filter(e => e.type === 'lesson_complete').length} color="pink" />
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Streak Distribution */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Streak Distribution</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={streakBuckets}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#ec4899" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Phase Distribution */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Users by Phase</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={phaseDistribution} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                          {phaseDistribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Top users by lessons */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Top Users by Lessons Completed</h3>
                  <div className="space-y-2">
                    {allUsers
                      .map(u => ({
                        ...u,
                        totalLessons: u.phaseProgress
                          ? Object.values(u.phaseProgress).reduce((s, p) => s + (p.completedLessons?.length || 0), 0)
                          : (u.completedLessons?.length || 0)
                      }))
                      .sort((a, b) => b.totalLessons - a.totalLessons)
                      .slice(0, 10)
                      .map((u, i) => (
                        <div key={u.uid} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-black text-gray-400 w-6">#{i + 1}</span>
                            <div>
                              <span className="font-bold text-gray-900 text-sm">{u.username || u.email}</span>
                              <span className="text-xs text-gray-400 ml-2">Phase {u.currentPhase || 1}</span>
                            </div>
                          </div>
                          <span className="font-bold text-purple-600">{u.totalLessons} lessons</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            )}

            {/* ═══ TRAFFIC TAB ═══ */}
            {activeTab === 'traffic' && (() => {
              // Computed data scoped to time range
              const fte = filteredTrafficEvents;
              const uniqueSessions = new Set(fte.map(e => e.sessionId)).size;
              const homeViews = fte.filter(e => e.type === 'page_view' && e.page === 'home').length;
              const tryViews = fte.filter(e => e.type === 'page_view' && e.page === 'try').length;
              const loginViews = fte.filter(e => e.type === 'page_view' && e.page === 'login').length;
              // Free-trial funnel — each screen owns exactly one page_view tag:
              //   free-activity-intro  = cold-lander intro
              //   free-activity        = age picker
              //   free-activity-lesson = the activity itself (anonymous)
              const freeTrialIntroViews = fte.filter(e => e.type === 'page_view' && e.page === 'free-activity-intro').length;
              const freeTrialAgePickerViews = fte.filter(e => e.type === 'page_view' && e.page === 'free-activity').length;
              const freeTrialLessonViews = fte.filter(e => e.type === 'page_view' && e.page === 'free-activity-lesson').length;
              // Total free-trial landing volume = anyone who reached either the
              // intro screen OR the age picker (warm CTAs skip the intro).
              const freeTrialViews = freeTrialIntroViews + freeTrialAgePickerViews;
              const freeTrialIntroContinues = fte.filter(e => e.type === 'cta_click' && e.metadata?.button === 'trial_intro_continue').length;
              const signupEvents = fte.filter(e => e.type === 'signup').length;
              const lessonStarts = fte.filter(e => e.type === 'lesson_start').length;
              const lessonCompletes = fte.filter(e => e.type === 'lesson_complete').length;

              // CTA click breakdown
              const ctaClicks = fte.filter(e => e.type === 'cta_click');
              const ctaByButton = {};
              const ctaByLocation = {};
              ctaClicks.forEach(e => {
                const btn = e.metadata?.button || 'unknown';
                const loc = e.metadata?.location || 'unknown';
                ctaByButton[btn] = (ctaByButton[btn] || 0) + 1;
                ctaByLocation[loc] = (ctaByLocation[loc] || 0) + 1;
              });
              const ctaButtonData = Object.entries(ctaByButton).map(([name, count]) => ({ name, clicks: count })).sort((a, b) => b.clicks - a.clicks);
              const ctaLocationData = Object.entries(ctaByLocation).map(([name, count]) => ({ name, clicks: count })).sort((a, b) => b.clicks - a.clicks);

              // Scroll depth
              const scrollEvents = fte.filter(e => e.type === 'scroll_depth');
              const scrollByDepth = { 25: 0, 50: 0, 75: 0, 100: 0 };
              scrollEvents.forEach(e => { const d = e.metadata?.depth; if (d && scrollByDepth[d] !== undefined) scrollByDepth[d]++; });
              const scrollData = Object.entries(scrollByDepth).map(([depth, count]) => ({ depth: `${depth}%`, sessions: count }));

              // Section views
              const sectionEvents = fte.filter(e => e.type === 'section_view');
              const sectionCounts = {};
              sectionEvents.forEach(e => { const s = e.metadata?.section || 'unknown'; sectionCounts[s] = (sectionCounts[s] || 0) + 1; });
              const sectionData = Object.entries(sectionCounts).map(([section, count]) => ({ section, views: count })).sort((a, b) => b.views - a.views);

              // Free trial age group breakdown
              const ageSelectEvents = fte.filter(e => e.type === 'cta_click' && e.metadata?.button === 'trial_age_select');
              const ageGroupCounts = { '1-2': 0, '2-3': 0, '3-4': 0, '4-5': 0 };
              ageSelectEvents.forEach(e => { const ag = e.metadata?.ageGroup; if (ag && ageGroupCounts[ag] !== undefined) ageGroupCounts[ag]++; });
              const trialCompletesByAge = { '1-2': 0, '2-3': 0, '3-4': 0, '4-5': 0 };
              fte.filter(e => e.type === 'lesson_complete' && e.metadata?.source === 'free_trial' && e.metadata?.ageGroup).forEach(e => {
                const ag = e.metadata.ageGroup;
                if (trialCompletesByAge[ag] !== undefined) trialCompletesByAge[ag]++;
              });

              // Device breakdown (by unique session)
              const deviceSessionsSeen = new Set();
              const deviceCounts = {};
              fte.forEach(e => {
                if (deviceSessionsSeen.has(e.sessionId)) return;
                deviceSessionsSeen.add(e.sessionId);
                const dt = e.deviceType || 'unknown';
                deviceCounts[dt] = (deviceCounts[dt] || 0) + 1;
              });
              const deviceData = Object.entries(deviceCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

              // Daily traffic trend
              const cutoffs = { '24h': 1, '7d': 7, '30d': 30, '90d': 90, 'all': 365 };
              const trendDays = Math.min(cutoffs[trafficTimeRange] || 30, 90);
              const trafficTrend = [];
              for (let i = trendDays - 1; i >= 0; i--) {
                const d = new Date(now); d.setDate(d.getDate() - i);
                const dateStr = d.toISOString().split('T')[0];
                const dayEvents = fte.filter(e => {
                  if (!e.timestamp) return false;
                  const ts = typeof e.timestamp === 'number' ? new Date(e.timestamp) : e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp);
                  return ts.toISOString().startsWith(dateStr);
                });
                const daySessions = new Set(dayEvents.map(e => e.sessionId)).size;
                trafficTrend.push({ date: `${d.getMonth() + 1}/${d.getDate()}`, sessions: daySessions, events: dayEvents.length });
              }

              // Funnel (time-filtered)
              // Landing base = home + /try (ad landing) + free-activity direct lands
              const totalLandings = homeViews + tryViews + freeTrialViews;
              const funnelBase = totalLandings || 1;
              // Hero pill (founder face) interactions — small parasocial trust
              // signal in the landing hero. Counts only the new event name; the
              // legacy 'founder_face_hero' label is kept in case any historical
              // events still reference it before the rename.
              const heroPillClicks =
                (ctaByButton['hero_pill_founder'] || 0) +
                (ctaByButton['founder_face_hero'] || 0);
              const funnelSteps = [
                { label: 'Landing Page Views', value: totalLandings, color: 'blue' },
                { label: '\u00A0\u00A0\u2514 Organic (/)', value: homeViews, color: 'blue' },
                { label: '\u00A0\u00A0\u2514 Ad Landing (/try)', value: tryViews, color: 'blue' },
                { label: 'Cold-Lander Intro Viewed', value: freeTrialIntroViews, color: 'blue' },
                { label: 'Continued Past Intro', value: freeTrialIntroContinues, color: 'indigo' },
                { label: 'Reached Age Picker', value: freeTrialAgePickerViews, color: 'indigo' },
                { label: 'Picked an Age (Started Activity)', value: freeTrialLessonViews, color: 'teal' },
                { label: 'Scrolled to Age Picker (home)', value: sectionCounts['age-picker'] || 0, color: 'indigo' },
                { label: 'Scrolled to Final CTA', value: sectionCounts['final-cta'] || 0, color: 'purple' },
                { label: 'Hero Pill (Founder) Clicks', value: heroPillClicks, color: 'purple' },
                { label: 'Clicked Free Trial / Age Select', value: (ctaByButton['free_trial'] || 0) + (ctaByButton['trial_age_select'] || 0), color: 'teal' },
                { label: 'Clicked Start Free', value: (ctaByButton['start_free'] || 0), color: 'pink' },
                { label: 'Visited Login Page', value: loginViews, color: 'orange' },
                { label: 'Signed Up', value: signupEvents, color: 'green' },
              ];

              return (
              <div>
                {/* Time Range Selector */}
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="text-sm font-semibold text-gray-500 mr-1">Time Range:</span>
                  {[
                    { id: '24h', label: '24h' },
                    { id: '7d', label: '7 Days' },
                    { id: '30d', label: '30 Days' },
                    { id: '90d', label: '90 Days' },
                    { id: 'all', label: 'All Time' },
                  ].map(range => (
                    <button
                      key={range.id}
                      onClick={() => setTrafficTimeRange(range.id)}
                      className={`px-4 py-1.5 rounded-full font-semibold text-sm transition ${
                        trafficTimeRange === range.id
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                          : 'bg-white text-gray-600 hover:bg-gray-50 shadow border border-gray-200'
                      }`}
                    >
                      {range.label}
                    </button>
                  ))}
                  <label className="flex items-center gap-1.5 ml-2 px-3 py-1.5 rounded-full text-sm font-semibold bg-white text-gray-600 shadow border border-gray-200 cursor-pointer hover:bg-gray-50 transition">
                    <input
                      type="checkbox"
                      checked={excludeDirect}
                      onChange={(e) => setExcludeDirect(e.target.checked)}
                      className="cursor-pointer"
                    />
                    Ignore direct visits
                  </label>
                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      onClick={() => exportAnalyticsCsv(fte, 'readbright-live-analytics')}
                      disabled={fte.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-white text-gray-600 hover:bg-gray-50 shadow border border-gray-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Download size={14} /> Export CSV
                    </button>
                    <button
                      onClick={() => setArchiveConfirm(true)}
                      disabled={analyticsEvents.length === 0 || archiving}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-white text-amber-600 hover:bg-amber-50 shadow border border-amber-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Archive size={14} /> Archive & Clear
                    </button>
                    <button
                      onClick={() => setClearConfirm(true)}
                      disabled={analyticsEvents.length === 0 || archiving}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-white text-red-600 hover:bg-red-50 shadow border border-red-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={14} /> Clear Data
                    </button>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard label="Sessions" value={uniqueSessions} color="purple" />
                  <StatCard label="Total Events" value={fte.length} color="blue" />
                  <StatCard label="CTA Clicks" value={ctaClicks.length} color="pink" />
                  <StatCard label="Signups" value={signupEvents} sub={totalLandings ? `${(signupEvents / totalLandings * 100).toFixed(1)}% of visitors` : ''} color="green" />
                </div>

                {/* Traffic Trend */}
                {trafficTrend.length > 1 && (
                  <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <h3 className="font-bold text-gray-900 mb-4">Traffic Trend</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={trafficTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={Math.max(0, Math.floor(trafficTrend.length / 10))} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="sessions" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} name="Sessions" />
                        <Area type="monotone" dataKey="events" stroke="#ec4899" fill="#ec4899" fillOpacity={0.1} name="Events" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Conversion Funnel (time-filtered) */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4">Conversion Funnel</h3>
                  <div className="space-y-3">
                    {funnelSteps.map((step, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 font-semibold">{step.label}</span>
                          <span className="font-bold text-gray-900">
                            {step.value}
                            <span className="text-gray-400 font-normal ml-1">
                              ({funnelBase ? Math.round(step.value / funnelBase * 100) : 0}%)
                            </span>
                            {i > 0 && funnelSteps[i - 1].value > 0 && (
                              <span className="text-xs text-gray-400 ml-1">
                                · {Math.round(step.value / funnelSteps[i - 1].value * 100)}% prev
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                          <div className={`bg-gradient-to-r from-${step.color}-400 to-${step.color}-600 h-2.5 rounded-full transition-all`} style={{ width: `${funnelBase ? Math.max(2, step.value / funnelBase * 100) : 0}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Referral Sources */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Traffic Sources</h3>
                    {referralData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={referralData.slice(0, 8)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="source" type="category" tick={{ fontSize: 11 }} width={100} />
                          <Tooltip />
                          <Bar dataKey="sessions" fill="#ec4899" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-8">No referral data yet.</p>
                    )}
                    <p className="text-xs text-gray-400 mt-3">Tip: Add <code className="bg-gray-100 px-1 rounded">?utm_source=instagram</code> to links.</p>
                  </div>

                  {/* Page Views */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Page Views</h3>
                    {pageViewData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={pageViewData.slice(0, 8)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="page" type="category" tick={{ fontSize: 11 }} width={80} />
                          <Tooltip />
                          <Bar dataKey="views" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-8">No page view data yet.</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  {/* CTA Clicks — Type & Location split */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">CTA Clicks</h3>
                    {ctaButtonData.length > 0 || ctaLocationData.length > 0 ? (
                      <div className="grid grid-cols-2 divide-x divide-gray-200">
                        <div className="pr-4">
                          <p className="text-xs font-bold text-gray-500 uppercase mb-2">By Type</p>
                          <div className="space-y-1.5">
                            {ctaButtonData.map((item, i) => (
                              <div key={i} className="flex justify-between items-center py-1 border-b border-gray-50 last:border-0">
                                <span className="text-sm text-gray-700 font-semibold">{item.name.replace(/_/g, ' ')}</span>
                                <span className="text-sm font-bold text-purple-600">{item.clicks}</span>
                              </div>
                            ))}
                            {ctaButtonData.length === 0 && <p className="text-gray-400 text-xs py-2">No data</p>}
                          </div>
                        </div>
                        <div className="pl-4">
                          <p className="text-xs font-bold text-gray-500 uppercase mb-2">By Location</p>
                          <div className="space-y-1.5">
                            {ctaLocationData.map((item, i) => (
                              <div key={i} className="flex justify-between items-center py-1 border-b border-gray-50 last:border-0">
                                <span className="text-sm text-gray-700 font-semibold">{item.name.replace(/_/g, ' ')}</span>
                                <span className="text-sm font-bold text-pink-600">{item.clicks}</span>
                              </div>
                            ))}
                            {ctaLocationData.length === 0 && <p className="text-gray-400 text-xs py-2">No data</p>}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-6">No CTA clicks tracked yet.</p>
                    )}
                  </div>

                  {/* Device Type */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Device Type</h3>
                    {deviceData.length > 0 && deviceData[0].name !== 'unknown' ? (
                      <div className="space-y-3">
                        {deviceData.map((item, i) => {
                          const total = deviceData.reduce((s, d) => s + d.value, 0) || 1;
                          const icons = { mobile: '📱', tablet: '📋', desktop: '💻' };
                          return (
                            <div key={i}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-700 font-semibold">{icons[item.name] || '❓'} {item.name}</span>
                                <span className="font-bold text-gray-900">{item.value} <span className="text-xs text-gray-400 font-normal">({Math.round(item.value / total * 100)}%)</span></span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div className="bg-gradient-to-r from-purple-400 to-pink-500 h-2 rounded-full transition-all" style={{ width: `${Math.round(item.value / total * 100)}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-6">Device data will appear for new events.</p>
                    )}
                  </div>

                  {/* Scroll Depth */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Scroll Depth (Homepage)</h3>
                    {scrollEvents.length > 0 ? (
                      <div className="space-y-3">
                        {scrollData.map((item, i) => (
                          <div key={i}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-700 font-semibold">{item.depth}</span>
                              <span className="font-bold text-gray-900">{item.sessions}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div className="bg-gradient-to-r from-blue-400 to-purple-500 h-2 rounded-full transition-all" style={{ width: `${scrollData[0]?.sessions ? (item.sessions / scrollData[0].sessions * 100) : 0}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-6">No scroll data yet.</p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  {/* Section Engagement */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Section Engagement (Homepage)</h3>
                    {sectionData.length > 0 ? (
                      <div className="space-y-2">
                        {sectionData.map((item, i) => (
                          <div key={i} className="flex justify-between items-center py-1.5 border-b border-gray-50 last:border-0">
                            <span className="text-sm text-gray-700 font-semibold capitalize">{item.section.replace(/-/g, ' ')}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-indigo-600">{item.views}</span>
                              <span className="text-xs text-gray-400">{homeViews ? `${Math.round(item.views / homeViews * 100)}%` : ''} of visitors</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-6">No section data yet. Visitors scrolling through sections will appear here.</p>
                    )}
                  </div>

                  {/* Lesson Engagement */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Lesson Engagement</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-blue-600">{lessonStarts}</p>
                        <p className="text-xs text-gray-600 font-semibold">Lessons Started</p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-green-600">{lessonCompletes}</p>
                        <p className="text-xs text-gray-600 font-semibold">Lessons Completed</p>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-purple-600">{lessonStarts ? `${Math.round(lessonCompletes / lessonStarts * 100)}%` : '0%'}</p>
                        <p className="text-xs text-gray-600 font-semibold">Completion Rate</p>
                      </div>
                      <div className="bg-pink-50 rounded-xl p-4 text-center">
                        <p className="text-2xl font-black text-pink-600">{freeTrialViews}</p>
                        <p className="text-xs text-gray-600 font-semibold">Free Activity Views</p>
                      </div>
                    </div>
                  </div>

                  {/* Free Trial Age Group Breakdown */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Free Trial Age Groups</h3>
                    <div className="space-y-3">
                      {[
                        { label: '1-2 yrs', key: '1-2', color: 'pink' },
                        { label: '2-3 yrs', key: '2-3', color: 'green' },
                        { label: '3-4 yrs', key: '3-4', color: 'blue' },
                        { label: '4-5 yrs', key: '4-5', color: 'purple' },
                      ].map(ag => {
                        const selected = ageGroupCounts[ag.key] || 0;
                        const completed = trialCompletesByAge[ag.key] || 0;
                        const totalSelections = Object.values(ageGroupCounts).reduce((a, b) => a + b, 0) || 1;
                        return (
                          <div key={ag.key} className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-700 w-16">{ag.label}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden relative">
                              <div className={`bg-${ag.color}-400 h-full rounded-full transition-all`} style={{ width: `${Math.round((selected / totalSelections) * 100)}%` }} />
                              <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">{selected} selected</span>
                            </div>
                            <span className="text-xs font-semibold text-gray-500 w-24 text-right">{completed} completed</span>
                          </div>
                        );
                      })}
                    </div>
                    {Object.values(ageGroupCounts).reduce((a, b) => a + b, 0) === 0 && (
                      <p className="text-gray-400 text-sm text-center mt-4">Age group data will appear once trial users select an age range.</p>
                    )}
                  </div>
                </div>

                {/* Recent Activity — grouped by session */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">Recent Activity Feed</h3>
                    {sessionGroups.length > 0 && (
                      <span className="text-xs text-gray-400">{sessionGroups.length} session{sessionGroups.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  {sessionGroups.length > 0 ? (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {sessionGroups.map((group) => {
                        const color = sessionColorMap[group.sessionId] || '#9ca3af';
                        const label = getSessionLabel(group.sessionId);
                        const first = group.events[0];
                        const last = group.events[group.events.length - 1];
                        return (
                          <div key={group.sessionId} className="rounded-lg overflow-hidden" style={{ border: `1px solid ${color}30` }}>
                            {/* Session header */}
                            <div className="flex items-center gap-2 px-3 py-2" style={{ backgroundColor: color + '12' }}>
                              <span
                                className="text-xs font-bold px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: color + '25', color }}
                              >
                                {label}
                              </span>
                              <span className="text-xs" title={first.deviceType || 'unknown'}>
                                {first.deviceType === 'mobile' ? '📱' : first.deviceType === 'tablet' ? '📲' : '🖥️'}
                              </span>
                              <span className="text-xs font-medium" style={{ color }}>{first.deviceType || 'unknown'}</span>
                              {first.referralSource && first.referralSource !== 'direct' && (
                                <span className="text-xs text-cyan-600 bg-cyan-50 rounded px-1.5 py-0.5">
                                  via {first.referralSource}
                                </span>
                              )}
                              <span className="text-[10px] text-gray-400 ml-auto">{group.events.length} event{group.events.length !== 1 ? 's' : ''} · {formatTimeAgo(last.timestamp)}</span>
                            </div>
                            {/* Events in this session (oldest first = timeline order) */}
                            <div>
                              {(() => {
                                const sessionConverted = group.events.some(e => e.type === 'signup' || e.type === 'login_success');
                                return group.events.map((event, j) => {
                                const isLoginPage = event.type === 'page_view' && event.page === 'login';
                                return (
                                <div key={j} className="flex items-center gap-2 py-1.5 px-3 border-t border-gray-50" style={{ borderLeft: `3px solid ${color}` }}>
                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    isLoginPage ? (sessionConverted ? 'bg-green-500' : 'bg-red-500') :
                                    event.type === 'signup' ? 'bg-green-500' :
                                    event.type === 'login_success' ? 'bg-green-500' :
                                    event.type === 'lesson_complete' ? 'bg-purple-500' :
                                    event.type === 'subscription_start' ? 'bg-yellow-500' :
                                    event.type === 'cta_click' ? 'bg-pink-500' :
                                    event.type === 'scroll_depth' ? 'bg-indigo-400' :
                                    event.type === 'section_view' ? 'bg-teal-400' :
                                    'bg-blue-500'
                                  }`} />
                                  <span className="text-xs font-semibold text-gray-500 w-24 flex-shrink-0">{event.type?.replace(/_/g, ' ')}</span>
                                  <span className="text-xs text-gray-600 truncate flex-1">
                                    {event.type === 'scroll_depth' && event.metadata?.depth != null
                                      ? `${event.metadata.depth}%`
                                      : event.page
                                      ? event.page
                                      : event.metadata?.button
                                      ? event.metadata.button.replace(/_/g, ' ')
                                      : event.metadata?.section
                                      ? event.metadata.section.replace(/_/g, ' ')
                                      : event.metadata?.lessonId != null
                                      ? `lesson ${event.metadata.lessonId}`
                                      : ''}
                                  </span>
                                  <NavBadge event={event} />
                                  <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{formatTimeAgo(event.timestamp)}</span>
                                </div>
                                );
                              });
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-8">No events tracked yet.</p>
                  )}
                </div>
              </div>
              );
            })()}

            {/* ═══ SUBSCRIPTIONS TAB ═══ */}
            {activeTab === 'subscriptions' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard label="Active Paid" value={paidUsers} color="green" />
                  <StatCard label="Monthly" value={monthlySubscribers} sub="$10.99/mo each" color="blue" />
                  <StatCard label="Annual" value={annualSubscribers} sub="$89/yr each" color="purple" />
                  <StatCard label="Est. MRR" value={`$${(monthlySubscribers * 10.99 + annualSubscribers * (89 / 12)).toFixed(0)}`} color="pink" />
                </div>

                {/* Paid users list */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4">Active Subscribers</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-bold text-gray-600">User</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-600">Plan</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-600">Status</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-600">Start Date</th>
                          <th className="px-4 py-2 text-left font-bold text-gray-600">Last Payment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {allUsers.filter(u => u.subscription === 'paid').map(u => (
                          <tr key={u.uid} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <div className="font-bold text-gray-900">{u.username || '—'}</div>
                              <div className="text-xs text-gray-400">{u.email}</div>
                            </td>
                            <td className="px-4 py-2">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.planType === 'annual' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {u.planType || 'monthly'}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                {u.subscriptionStatus || 'active'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500">{formatDate(u.subscriptionStartDate)}</td>
                            <td className="px-4 py-2 text-xs text-gray-500">{formatDate(u.lastPaymentDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {paidUsers === 0 && <p className="text-gray-400 text-sm text-center py-4">No paid subscribers yet.</p>}
                </div>

                {/* Recent sub events */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Recent Subscription Events</h3>
                  {subscriptionEvents.length > 0 ? (
                    <div className="space-y-2">
                      {subscriptionEvents.slice(0, 20).map((event, i) => (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50">
                          <div className={`w-2 h-2 rounded-full ${event.type === 'subscription_start' ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-sm font-semibold text-gray-700">{event.type === 'subscription_start' ? 'New subscription' : 'Cancellation'}</span>
                          <span className="text-xs text-gray-400 ml-auto">{formatTimeAgo(event.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">Subscription events will appear here as they happen.</p>
                  )}
                </div>
              </div>
            )}

            {/* ═══ ARCHIVES TAB ═══ */}
            {activeTab === 'archives' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <StatCard label="Total Archives" value={archives.length} color="amber" />
                  <StatCard label="Total Archived Events" value={archives.reduce((s, a) => s + (a.eventCount || 0), 0)} color="purple" />
                  <StatCard label="Live Events" value={analyticsEvents.length} color="blue" />
                </div>

                {archives.length > 0 ? (
                  <div className="space-y-4">
                    {archives.map((archive) => {
                      const isViewing = viewingArchive === archive.id;
                      const startDate = archive.dateRangeStart ? new Date(archive.dateRangeStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                      const endDate = archive.dateRangeEnd ? new Date(archive.dateRangeEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                      const archivedDate = archive.archivedAt ? new Date(archive.archivedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

                      // Build summary stats for this archive
                      const events = archive.events || [];
                      const types = {};
                      const pages = {};
                      const sources = {};
                      const sourceSessionsSeen = new Set();
                      const ctaButtons = {};
                      const sessions = new Set();
                      const dailyCounts = {};

                      events.forEach(e => {
                        types[e.type] = (types[e.type] || 0) + 1;
                        if (e.sessionId) sessions.add(e.sessionId);
                        if (e.type === 'page_view' && e.page) {
                          pages[e.page] = (pages[e.page] || 0) + 1;
                        }
                        if (e.type === 'cta_click' && e.metadata?.button) ctaButtons[e.metadata.button] = (ctaButtons[e.metadata.button] || 0) + 1;
                        const src = (e.referralSource || 'direct').toLowerCase();
                        if (src && src !== 'localhost' && src !== '127.0.0.1') {
                          const key = `${e.sessionId}_${src}`;
                          if (!sourceSessionsSeen.has(key)) { sourceSessionsSeen.add(key); sources[src] = (sources[src] || 0) + 1; }
                        }
                        const ds = e.timestamp ? e.timestamp.split('T')[0] : null;
                        if (ds) {
                          if (!dailyCounts[ds]) dailyCounts[ds] = { events: 0, sessions: new Set() };
                          dailyCounts[ds].events++;
                          if (e.sessionId) dailyCounts[ds].sessions.add(e.sessionId);
                        }
                      });

                      const sortedTypes = Object.entries(types).sort((a, b) => b[1] - a[1]);
                      const sortedPages = Object.entries(pages).sort((a, b) => b[1] - a[1]);
                      const sortedSources = Object.entries(sources).sort((a, b) => b[1] - a[1]);
                      const sortedCta = Object.entries(ctaButtons).sort((a, b) => b[1] - a[1]);
                      const sortedDays = Object.entries(dailyCounts).sort((a, b) => a[0].localeCompare(b[0]));

                      // Funnel — landing base includes home + /try + free activity (ad traffic)
                      const archiveHomeViews = pages['home'] || 0;
                      const archiveTryViews = pages['try'] || 0;
                      const archiveFreeViews = pages['free activity'] || 0;
                      const archiveTotalLandings = archiveHomeViews + archiveTryViews + archiveFreeViews;
                      const funnelBase = archiveTotalLandings || 1;
                      const funnelSteps = [
                        { label: 'Landing Page Views', value: archiveTotalLandings },
                        { label: 'Hero Pill (Founder) Clicks', value: (ctaButtons['hero_pill_founder'] || 0) + (ctaButtons['founder_face_hero'] || 0) },
                        { label: 'Clicked Free Trial / Age Select', value: (ctaButtons['free_trial'] || 0) + (ctaButtons['trial_age_select'] || 0) },
                        { label: 'Clicked Start Free', value: ctaButtons['start_free'] || 0 },
                        { label: 'Visited Login', value: pages['login'] || 0 },
                        { label: 'Signed Up', value: types['signup'] || 0 },
                      ];

                      return (
                        <div key={archive.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                          {/* Archive Header */}
                          <div
                            className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 transition"
                            onClick={() => setViewingArchive(isViewing ? null : archive.id)}
                          >
                            <div className="flex items-center gap-4">
                              <div className="bg-amber-100 p-2.5 rounded-xl">
                                <Archive className="text-amber-600" size={20} />
                              </div>
                              <div>
                                <h3 className="font-bold text-gray-900">
                                  {startDate} — {endDate}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Archived {archivedDate} &middot; {archive.eventCount} events &middot; {sessions.size} sessions
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(ev) => { ev.stopPropagation(); exportAnalyticsCsv(events, `readbright-archive-${startDate}`); }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                              >
                                <Download size={12} /> Export
                              </button>
                              {isViewing ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                            </div>
                          </div>

                          {/* Expanded Archive Detail */}
                          {isViewing && (
                            <div className="border-t border-gray-100 p-5 space-y-6">
                              {/* Summary Stats */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-purple-50 rounded-xl p-3 text-center">
                                  <p className="text-xl font-black text-purple-600">{sessions.size}</p>
                                  <p className="text-xs text-gray-600 font-semibold">Sessions</p>
                                </div>
                                <div className="bg-blue-50 rounded-xl p-3 text-center">
                                  <p className="text-xl font-black text-blue-600">{events.length}</p>
                                  <p className="text-xs text-gray-600 font-semibold">Events</p>
                                </div>
                                <div className="bg-pink-50 rounded-xl p-3 text-center">
                                  <p className="text-xl font-black text-pink-600">{types['cta_click'] || 0}</p>
                                  <p className="text-xs text-gray-600 font-semibold">CTA Clicks</p>
                                </div>
                                <div className="bg-green-50 rounded-xl p-3 text-center">
                                  <p className="text-xl font-black text-green-600">{types['signup'] || 0}</p>
                                  <p className="text-xs text-gray-600 font-semibold">Signups</p>
                                </div>
                              </div>

                              {/* Event Type Breakdown */}
                              <div className="grid md:grid-cols-2 gap-5">
                                <div>
                                  <h4 className="font-bold text-gray-800 text-sm mb-3">Event Breakdown</h4>
                                  <div className="space-y-1.5">
                                    {sortedTypes.map(([type, count]) => (
                                      <div key={type} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                                        <span className="text-xs text-gray-700 font-semibold">{type.replace(/_/g, ' ')}</span>
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-bold text-purple-600">{count}</span>
                                          <span className="text-xs text-gray-400">{(count / events.length * 100).toFixed(0)}%</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div>
                                  <h4 className="font-bold text-gray-800 text-sm mb-3">Page Views</h4>
                                  {sortedPages.length > 0 ? (
                                    <div className="space-y-1.5">
                                      {sortedPages.map(([page, count]) => {
                                        const totalPV = Object.values(pages).reduce((a, b) => a + b, 0) || 1;
                                        return (
                                          <div key={page} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                                            <span className="text-xs text-gray-700 font-semibold">{page}</span>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs font-bold text-blue-600">{count}</span>
                                              <span className="text-xs text-gray-400">{(count / totalPV * 100).toFixed(0)}%</span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400">No page view data</p>
                                  )}
                                </div>
                              </div>

                              {/* Traffic Sources + CTA */}
                              <div className="grid md:grid-cols-2 gap-5">
                                <div>
                                  <h4 className="font-bold text-gray-800 text-sm mb-3">Traffic Sources</h4>
                                  {sortedSources.length > 0 ? (
                                    <div className="space-y-1.5">
                                      {sortedSources.slice(0, 8).map(([source, count]) => (
                                        <div key={source} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                                          <span className="text-xs text-gray-700 font-semibold">{source}</span>
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold text-pink-600">{count}</span>
                                            <span className="text-xs text-gray-400">{(count / sessions.size * 100).toFixed(0)}%</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400">No referral data</p>
                                  )}
                                </div>

                                <div>
                                  <h4 className="font-bold text-gray-800 text-sm mb-3">CTA Clicks</h4>
                                  {sortedCta.length > 0 ? (
                                    <div className="space-y-1.5">
                                      {sortedCta.map(([btn, count]) => (
                                        <div key={btn} className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0">
                                          <span className="text-xs text-gray-700 font-semibold">{btn.replace(/_/g, ' ')}</span>
                                          <span className="text-xs font-bold text-teal-600">{count}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400">No CTA data</p>
                                  )}
                                </div>
                              </div>

                              {/* Conversion Funnel */}
                              {homeViews > 0 && (
                                <div>
                                  <h4 className="font-bold text-gray-800 text-sm mb-3">Conversion Funnel</h4>
                                  <div className="space-y-2">
                                    {funnelSteps.map((step, i) => (
                                      <div key={i}>
                                        <div className="flex justify-between text-xs mb-0.5">
                                          <span className="text-gray-700 font-semibold">{step.label}</span>
                                          <span className="font-bold text-gray-900">
                                            {step.value}
                                            <span className="text-gray-400 font-normal ml-1">
                                              ({Math.round(step.value / funnelBase * 100)}%)
                                            </span>
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2">
                                          <div className="bg-gradient-to-r from-purple-400 to-pink-500 h-2 rounded-full transition-all" style={{ width: `${Math.max(2, step.value / funnelBase * 100)}%` }} />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Daily Trend */}
                              {sortedDays.length > 1 && (
                                <div>
                                  <h4 className="font-bold text-gray-800 text-sm mb-3">Daily Activity</h4>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-3 py-1.5 text-left font-bold text-gray-600">Date</th>
                                          <th className="px-3 py-1.5 text-right font-bold text-gray-600">Sessions</th>
                                          <th className="px-3 py-1.5 text-right font-bold text-gray-600">Events</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-50">
                                        {sortedDays.map(([date, data]) => (
                                          <tr key={date} className="hover:bg-gray-50">
                                            <td className="px-3 py-1.5 text-gray-700 font-medium">{date}</td>
                                            <td className="px-3 py-1.5 text-right font-bold text-purple-600">{data.sessions.size}</td>
                                            <td className="px-3 py-1.5 text-right font-bold text-blue-600">{data.events}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-md p-4 sm:p-8 text-center">
                    <Archive className="mx-auto text-gray-300 mb-3" size={40} />
                    <p className="text-gray-400 text-sm">No archived data yet. Use "Archive & Clear" on the Traffic tab to snapshot your current data.</p>
                  </div>
                )}
              </div>
            )}

            {/* ═══ FEEDBACK TAB ═══ */}
            {activeTab === 'feedback' && (() => {
              const feedbackByAge = { '1-2': 0, '2-3': 0, '3-4': 0, '4-5': 0, 'unknown': 0 };
              allFeedback.filter(f => f.source === 'free_trial').forEach(f => {
                const ag = f.ageGroup;
                if (ag && feedbackByAge[ag] !== undefined) feedbackByAge[ag]++;
                else feedbackByAge['unknown']++;
              });
              return (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <StatCard label="Total Feedback" value={allFeedback.length} color="purple" />
                  <StatCard label="Contact Forms" value={contactSubmissions.length} color="blue" />
                  <StatCard label="This Week" value={allFeedback.filter(f => f.createdAt && (new Date().getTime() - new Date(f.createdAt).getTime()) < 7 * 24 * 60 * 60 * 1000).length} color="pink" />
                  <StatCard label="From Trial" value={allFeedback.filter(f => f.source === 'free_trial').length} color="green" />
                  <StatCard label="New Contacts" value={contactSubmissions.filter(c => c.status === 'new').length} color="orange" />
                </div>

                {/* Trial Feedback by Age Group */}
                {allFeedback.some(f => f.source === 'free_trial') && (
                  <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <h3 className="font-bold text-gray-900 mb-4">Trial Feedback by Age Group</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: '1-2 yrs', key: '1-2', color: 'pink' },
                        { label: '2-3 yrs', key: '2-3', color: 'green' },
                        { label: '3-4 yrs', key: '3-4', color: 'blue' },
                        { label: '4-5 yrs', key: '4-5', color: 'purple' },
                      ].map(ag => (
                        <div key={ag.key} className={`bg-${ag.color}-50 rounded-xl p-4 text-center`}>
                          <p className={`text-2xl font-black text-${ag.color}-600`}>{feedbackByAge[ag.key]}</p>
                          <p className="text-xs text-gray-600 font-semibold">{ag.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Submissions */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Mail className="text-blue-600" size={20} />
                    Contact Form Submissions
                    {contactSubmissions.filter(c => c.status === 'new').length > 0 && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                        {contactSubmissions.filter(c => c.status === 'new').length} new
                      </span>
                    )}
                  </h3>
                  {contactSubmissions.length > 0 ? (
                    <div className="space-y-4">
                      {contactSubmissions.map((c) => (
                        <div key={c.id} className={`border rounded-xl p-5 hover:border-blue-200 transition ${c.status === 'new' ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-bold text-gray-900 text-sm">{c.name}</span>
                                <span className="text-xs text-gray-500">{c.email}</span>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                  c.subject === 'Bug Report' ? 'bg-red-100 text-red-700' :
                                  c.subject === 'Feature Request' ? 'bg-purple-100 text-purple-700' :
                                  c.subject === 'Technical Support' ? 'bg-orange-100 text-orange-700' :
                                  c.subject === 'Billing Question' ? 'bg-green-100 text-green-700' :
                                  c.subject === 'Partnership' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {c.subject || 'General Inquiry'}
                                </span>
                                {c.status === 'new' && (
                                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">NEW</span>
                                )}
                              </div>
                              <p className="text-gray-800 text-sm leading-relaxed">{c.message}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-gray-400">{c.createdAt ? formatDate(c.createdAt) : '—'}</span>
                            {contactDeleteConfirm === c.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-red-500 font-medium">Delete?</span>
                                <button
                                  onClick={() => handleDeleteContact(c.id)}
                                  disabled={contactDeleting}
                                  className="text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition disabled:opacity-50"
                                >
                                  {contactDeleting ? 'Deleting...' : 'Yes'}
                                </button>
                                <button
                                  onClick={() => setContactDeleteConfirm(null)}
                                  className="text-xs font-bold px-2 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setContactDeleteConfirm(c.id)}
                                className="text-gray-300 hover:text-red-500 transition"
                                title="Delete contact submission"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-8">No contact form submissions yet. Submissions from the contact page will appear here.</p>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MessageSquare className="text-purple-600" size={20} />
                    All Feedback
                  </h3>
                  {allFeedback.length > 0 ? (
                    <div className="space-y-4">
                      {allFeedback.map((fb) => (
                        <div key={fb.id} className="border border-gray-200 rounded-xl p-5 hover:border-purple-200 transition">
                          <div className="flex items-start justify-between gap-4">
                            <p className="text-gray-800 text-sm leading-relaxed flex-1">{fb.message}</p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {fb.ageGroup && (
                                <span className="text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                                  {fb.ageGroup} yrs
                                </span>
                              )}
                              <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                fb.source === 'free_trial' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {fb.source === 'free_trial' ? 'Free Trial' : fb.source || 'Unknown'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-gray-400">{fb.createdAt ? formatDate(fb.createdAt) : '—'}</span>
                            {feedbackDeleteConfirm === fb.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-red-500 font-medium">Delete?</span>
                                <button
                                  onClick={() => handleDeleteFeedback(fb.id)}
                                  disabled={feedbackDeleting}
                                  className="text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition disabled:opacity-50"
                                >
                                  {feedbackDeleting ? 'Deleting...' : 'Yes'}
                                </button>
                                <button
                                  onClick={() => setFeedbackDeleteConfirm(null)}
                                  className="text-xs font-bold px-2 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setFeedbackDeleteConfirm(fb.id)}
                                className="text-gray-300 hover:text-red-500 transition"
                                title="Delete feedback"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-8">No feedback received yet. Feedback from free trial users will appear here.</p>
                  )}
                </div>

                {/* User Reviews */}
                <div className="bg-white rounded-xl shadow-md p-6 mt-6">
                  <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <Star className="text-yellow-500" size={20} />
                    User Reviews
                  </h3>
                  {allReviews.length > 0 && (
                    <p className="text-xs text-gray-400 mb-4">
                      Avg: {(allReviews.reduce((s, r) => s + (r.rating || 0), 0) / allReviews.length).toFixed(1)} / 5 from {allReviews.length} review{allReviews.length !== 1 ? 's' : ''}
                    </p>
                  )}
                  {allReviews.length > 0 ? (
                    <div className="space-y-4">
                      {allReviews.map((rv) => (
                        <div key={rv.id} className="border border-gray-200 rounded-xl p-5 hover:border-yellow-200 transition">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1.5">
                                <div className="flex gap-0.5">
                                  {[1, 2, 3, 4, 5].map((s) => (
                                    <Star key={s} size={14} className={s <= (rv.rating || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'} />
                                  ))}
                                </div>
                                <span className="text-xs font-bold text-gray-700">{rv.username || rv.email || 'Anonymous'}</span>
                              </div>
                              {rv.message && (
                                <p className="text-gray-800 text-sm leading-relaxed">{rv.message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-gray-400">{rv.createdAt ? formatDate(rv.createdAt) : '—'}</span>
                            {reviewDeleteConfirm === rv.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-red-500 font-medium">Delete?</span>
                                <button
                                  onClick={() => handleDeleteReview(rv.id)}
                                  disabled={reviewDeleting}
                                  className="text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition disabled:opacity-50"
                                >
                                  {reviewDeleting ? 'Deleting...' : 'Yes'}
                                </button>
                                <button
                                  onClick={() => setReviewDeleteConfirm(null)}
                                  className="text-xs font-bold px-2 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setReviewDeleteConfirm(rv.id)}
                                className="text-gray-300 hover:text-red-500 transition"
                                title="Delete review"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-8 mt-2">No reviews yet. Users are prompted to review after completing 3 activities.</p>
                  )}
                </div>
              </div>
              );
            })()}

            {/* ═══ EMAIL TAB ═══ */}
            {activeTab === 'email' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <StatCard label="Email-Friendly Users" value={realUsers.filter(u => u.emailOptIn).length} color="green" />
                  <StatCard label="Total Users" value={totalUsers} color="purple" />
                  <StatCard label="Opt-In Rate" value={`${totalUsers ? Math.round(realUsers.filter(u => u.emailOptIn).length / totalUsers * 100) : 0}%`} color="blue" />
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Mail className="text-purple-600" size={20} />
                    Compose Email
                  </h3>

                  {/* Audience selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">Audience</label>
                    <select value={emailAudience} onChange={e => setEmailAudience(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-purple-400 outline-none">
                      <option value="emailOptIn">Email-Friendly Only ({realUsers.filter(u => u.emailOptIn).length})</option>
                      <option value="all">All Users ({totalUsers})</option>
                      <option value="free">Free Users ({freeUsers})</option>
                      <option value="paid">Paid Users ({paidUsers})</option>
                    </select>
                  </div>

                  {/* Subject */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">Subject</label>
                    <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                      placeholder="How was your first activity?"
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-purple-400 outline-none" />
                  </div>

                  {/* Body */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold text-gray-700 mb-2">Body (HTML supported)</label>
                    <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)}
                      placeholder="Hi there! We noticed you tried your first BrightReading activity — we'd love to hear how it went..."
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-purple-400 outline-none resize-none h-48" />
                    <p className="text-xs text-gray-400 mt-1">Use {'{{username}}'} to personalize. HTML tags are supported.</p>
                  </div>

                  {/* Preview */}
                  {emailBody && (
                    <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <p className="text-xs font-bold text-gray-500 mb-2 uppercase">Preview</p>
                      <p className="text-sm font-bold text-gray-900 mb-1">{emailSubject || '(no subject)'}</p>
                      <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: sanitizeHtml(emailBody.replace(/\{\{username\}\}/g, 'Sarah')) }} />
                    </div>
                  )}

                  {/* Send button */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={async () => {
                        if (!emailSubject.trim() || !emailBody.trim()) return;
                        const audienceUsers = allUsers.filter(u => {
                          if (!u.email) return false;
                          if (emailAudience === 'emailOptIn') return u.emailOptIn === true;
                          if (emailAudience === 'free') return u.subscription !== 'paid';
                          if (emailAudience === 'paid') return u.subscription === 'paid';
                          return true;
                        });
                        if (audienceUsers.length === 0) { setEmailResult({ type: 'error', text: 'No recipients match this audience.' }); return; }
                        if (!window.confirm(`Send "${emailSubject}" to ${audienceUsers.length} recipients?`)) return;
                        setEmailSending(true);
                        setEmailResult(null);
                        try {
                          const resp = await fetch(`https://us-central1-learning-app-6f8ff.cloudfunctions.net/sendBulkEmail`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              adminUid: user.uid,
                              recipients: audienceUsers.map(u => u.email),
                              subject: emailSubject,
                              body: audienceUsers.length > 0 ? emailBody : emailBody // personalization done server-side if needed
                            })
                          });
                          const data = await resp.json();
                          if (resp.ok) {
                            setEmailResult({ type: 'success', text: `Sent to ${data.sent} recipients${data.failed ? ` (${data.failed} failed)` : ''}` });
                            setEmailSubject('');
                            setEmailBody('');
                          } else {
                            setEmailResult({ type: 'error', text: data.error || 'Failed to send' });
                          }
                        } catch (err) {
                          setEmailResult({ type: 'error', text: err.message });
                        }
                        setEmailSending(false);
                      }}
                      disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-bold text-sm hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {emailSending ? (
                        <><RefreshCw size={16} className="animate-spin" /> Sending...</>
                      ) : (
                        <><Mail size={16} /> Send Email</>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        const audienceUsers = allUsers.filter(u => {
                          if (!u.email) return false;
                          if (emailAudience === 'emailOptIn') return u.emailOptIn === true;
                          if (emailAudience === 'free') return u.subscription !== 'paid';
                          if (emailAudience === 'paid') return u.subscription === 'paid';
                          return true;
                        });
                        navigator.clipboard.writeText(audienceUsers.map(u => u.email).join('\n'));
                        setEmailResult({ type: 'success', text: `Copied ${audienceUsers.length} emails to clipboard` });
                        setTimeout(() => setEmailResult(null), 2000);
                      }}
                      className="bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-bold text-sm hover:bg-gray-200 transition flex items-center gap-2"
                    >
                      <Copy size={16} /> Copy Emails
                    </button>
                  </div>

                  {emailResult && (
                    <div className={`mt-4 p-3 rounded-lg text-sm font-semibold ${emailResult.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                      {emailResult.text}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ═══ ADMIN MANAGEMENT TAB (Owner Only) ═══ */}
            {activeTab === 'admin' && isOwnerRole(userData?.role) && (
              <div>
                {actionMessage && (
                  <div className={`mb-4 p-4 rounded-xl text-sm font-semibold ${actionMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {actionMessage.text}
                  </div>
                )}

                {/* Promote admin */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Crown className="text-yellow-500" size={20} />
                    Promote User to Admin
                  </h3>
                  <div className="flex gap-3">
                    <input type="email" placeholder="Enter user email to promote..." value={promoteEmail} onChange={e => setPromoteEmail(e.target.value)}
                      className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-400 outline-none text-sm" />
                    <button onClick={handlePromote}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-bold text-sm hover:shadow-lg transition">
                      Promote
                    </button>
                  </div>
                </div>

                {/* Grant Paid Access */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="text-green-500" size={20} />
                    Manage Paid Access
                  </h3>
                  <p className="text-xs text-gray-400 mb-4">Grant or revoke paid access without Stripe. Persists until manually revoked.</p>
                  <div className="space-y-3">
                    {allUsers.filter(u => u.role !== 'owner').map(u => (
                      <div key={u.uid} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <div>
                            <span className="font-bold text-gray-900 text-sm">{u.username || '—'}</span>
                            <span className="text-xs text-gray-400 ml-2">{u.email}</span>
                          </div>
                          {u.adminGranted && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">admin-granted</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.subscription === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {u.subscription === 'paid' ? u.planType || 'paid' : 'free'}
                          </span>
                          {u.subscription === 'paid' && u.adminGranted ? (
                            <button onClick={() => handleRevokePaid(u.uid, u.email)}
                              className="text-red-600 hover:bg-red-50 px-3 py-1 rounded-lg text-xs font-bold transition">
                              Revoke Paid
                            </button>
                          ) : u.subscription !== 'paid' ? (
                            <button onClick={() => handleGrantPaid(u.uid, u.email)}
                              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 rounded-lg text-xs font-bold hover:shadow-lg transition">
                              Grant Paid
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Stripe sub</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Current admins */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4">Current Admins</h3>
                  <div className="space-y-3">
                    {allUsers.filter(u => u.role === 'owner' || u.role === 'admin').map(u => (
                      <div key={u.uid} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.role === 'owner' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                            {u.role === 'owner' ? '👑 Owner' : '🛡️ Admin'}
                          </span>
                          <div>
                            <span className="font-bold text-gray-900 text-sm">{u.username || '—'}</span>
                            <span className="text-xs text-gray-400 ml-2">{u.email}</span>
                          </div>
                        </div>
                        {u.role === 'admin' && (
                          <button onClick={() => handleRevoke(u.uid, u.email)}
                            className="text-red-600 hover:bg-red-50 px-3 py-1 rounded-lg text-xs font-bold transition">
                            Revoke
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Admin action log */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Admin Action Log</h3>
                  {adminLogs.length > 0 ? (
                    <div className="space-y-2">
                      {adminLogs.map((log, i) => (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                          <div className={`w-2 h-2 rounded-full ${log.action === 'promote_admin' ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-sm text-gray-700">{log.details}</span>
                          <span className="text-xs text-gray-400 ml-auto">{formatDate(log.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">No admin actions logged yet.</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── REFERRALS TAB ── */}
        {activeTab === 'referrals' && (() => {
          const usersWithCodes = allUsers.filter(u => u.referralCode);
          const referredUsers = allUsers.filter(u => u.referredBy);
          const referredPaid = referredUsers.filter(u => u.subscription === 'paid');
          const convRate = referredUsers.length > 0 ? Math.round((referredPaid.length / referredUsers.length) * 100) : 0;

          const codeStats = usersWithCodes.map(owner => {
            const signups = allUsers.filter(u => u.referredBy === owner.referralCode);
            const paid = signups.filter(u => u.subscription === 'paid');
            return {
              code: owner.referralCode,
              ownerUid: owner.uid,
              ownerUsername: owner.username || '—',
              ownerEmail: owner.email,
              assignedAt: owner.referralCodeAssignedAt,
              totalSignups: signups.length,
              paidSignups: paid.length,
              freeSignups: signups.length - paid.length,
              convRate: signups.length > 0 ? Math.round((paid.length / signups.length) * 100) : 0,
            };
          }).sort((a, b) => b.totalSignups - a.totalSignups);

          const barData = codeStats.slice(0, 10).map(s => ({ name: s.code, Paid: s.paidSignups, Free: s.freeSignups }));
          const pieData = [
            { name: 'Paid', value: referredPaid.length },
            { name: 'Free', value: referredUsers.length - referredPaid.length },
          ];

          const filteredUsers = allUsers
            .filter(u => u.role !== 'owner' && (
              u.username?.toLowerCase().includes(referralSearch.toLowerCase()) ||
              u.email?.toLowerCase().includes(referralSearch.toLowerCase())
            ))
            .slice(0, 8);

          return (
            <div className="space-y-6">
              {/* Summary stat cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatCard label="Active Codes" value={usersWithCodes.length} color="purple" />
                <StatCard label="Total Referred" value={referredUsers.length} color="pink" />
                <StatCard label="Paid Converts" value={referredPaid.length} color="green" sub={`${convRate}% conversion`} />
                <StatCard label="Organic Signups" value={allUsers.filter(u => !u.referredBy && !isAdmin(u.role)).length} color="blue" />
              </div>

              {referralActionMsg && (
                <div className={`p-4 rounded-xl text-sm font-semibold ${referralActionMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {referralActionMsg.text}
                </div>
              )}

              {/* Assign code */}
              <div className="bg-white rounded-2xl shadow-md p-6">
                <h3 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <Link2 className="text-purple-500" size={20} />
                  Assign Referral Code
                </h3>
                <p className="text-xs text-gray-400 mb-4">Assign a unique code to a user. Their link: <span className="font-mono text-purple-600">{window.location.origin}/?ref=CODE</span></p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Search user by username or email..."
                      value={referralSearch}
                      onChange={e => { setReferralSearch(e.target.value); setReferralAssignUid(''); }}
                      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-400 outline-none text-sm"
                    />
                    {referralSearch && !referralAssignUid && filteredUsers.length > 0 && (
                      <div className="absolute top-full mt-1 left-0 right-0 border border-gray-200 rounded-lg shadow-lg bg-white max-h-44 overflow-y-auto z-20">
                        {filteredUsers.map(u => (
                          <button key={u.uid} onClick={() => {
                            setReferralAssignUid(u.uid);
                            setReferralSearch(u.username || u.email);
                            if (!referralCodeInput) setReferralCodeInput((u.username || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12));
                          }} className="w-full text-left px-4 py-2.5 hover:bg-purple-50 text-sm flex items-center justify-between gap-2 border-b border-gray-50 last:border-0">
                            <span className="font-semibold text-gray-900">{u.username || '—'}</span>
                            <span className="text-xs text-gray-400 truncate">{u.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="CODE (e.g. SARAH2024)"
                    value={referralCodeInput}
                    onChange={e => setReferralCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20))}
                    className="w-full sm:w-52 px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-400 outline-none text-sm font-mono tracking-widest"
                  />
                  <button
                    onClick={handleAssignReferralCode}
                    disabled={referralAssigning || !referralAssignUid || !referralCodeInput}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:shadow-lg transition disabled:opacity-40"
                  >
                    {referralAssigning ? 'Assigning…' : 'Assign'}
                  </button>
                </div>
              </div>

              {/* Code table */}
              {codeStats.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-md p-10 text-center">
                  <Link2 className="mx-auto mb-3 text-gray-200" size={36} />
                  <p className="text-gray-400 font-semibold">No referral codes assigned yet</p>
                  <p className="text-gray-300 text-sm mt-1">Use the form above to create one</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-md overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                    <Link2 className="text-purple-500" size={18} />
                    <h3 className="font-bold text-gray-900">All Referral Codes</h3>
                    <span className="text-xs text-gray-400 ml-auto">{codeStats.length} active</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                          <th className="px-5 py-3 text-left">Code</th>
                          <th className="px-5 py-3 text-left">Owner</th>
                          <th className="px-5 py-3 text-left">Referral Link</th>
                          <th className="px-5 py-3 text-center">Signups</th>
                          <th className="px-5 py-3 text-center">Paid</th>
                          <th className="px-5 py-3 text-center">Free</th>
                          <th className="px-5 py-3 text-center">Conv %</th>
                          <th className="px-5 py-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {codeStats.map(s => {
                          const link = `${window.location.origin}/?ref=${s.code}`;
                          return (
                            <tr key={s.code} className="hover:bg-gray-50 transition">
                              <td className="px-5 py-4">
                                <span className="font-mono font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded text-xs">{s.code}</span>
                              </td>
                              <td className="px-5 py-4">
                                <div className="font-semibold text-gray-900 text-sm">{s.ownerUsername}</div>
                                <div className="text-xs text-gray-400">{s.ownerEmail}</div>
                              </td>
                              <td className="px-5 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 font-mono truncate max-w-[150px]">{link}</span>
                                  <button onClick={() => { navigator.clipboard.writeText(link); setCopiedCode(s.code); setTimeout(() => setCopiedCode(null), 2000); }} className="flex-shrink-0 text-purple-400 hover:text-purple-600 transition">
                                    {copiedCode === s.code ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                                  </button>
                                </div>
                              </td>
                              <td className="px-5 py-4 text-center font-bold text-gray-900">{s.totalSignups}</td>
                              <td className="px-5 py-4 text-center font-bold text-green-600">{s.paidSignups}</td>
                              <td className="px-5 py-4 text-center text-gray-500">{s.freeSignups}</td>
                              <td className="px-5 py-4 text-center">
                                <span className={`font-bold ${s.convRate >= 50 ? 'text-green-600' : s.convRate >= 20 ? 'text-yellow-600' : 'text-gray-400'}`}>
                                  {s.convRate}%
                                </span>
                              </td>
                              <td className="px-5 py-4 text-right">
                                <button onClick={() => handleRemoveReferralCode(s.ownerUid)} className="text-red-500 hover:bg-red-50 px-3 py-1 rounded-lg text-xs font-bold transition">
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Charts */}
              {codeStats.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4 text-sm">Signups by Code (Paid vs Free)</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={barData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Paid" stackId="a" fill="#10b981" />
                        <Bar dataKey="Free" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="bg-white rounded-2xl shadow-md p-6">
                    <h3 className="font-bold text-gray-900 mb-4 text-sm">All Referred Users: Paid vs Free</h3>
                    {referredUsers.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                            {pieData.map((_, i) => <Cell key={i} fill={['#10b981', '#8b5cf6'][i % 2]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[220px] text-gray-300 text-sm">No referred signups yet</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Archive Confirmation Modal */}
      {archiveConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-100 p-3 rounded-full">
                <Archive className="text-amber-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Archive Analytics Data</h3>
            </div>

            <p className="text-gray-600 mb-2">
              This will snapshot all <span className="font-bold">{analyticsEvents.length} events</span> into an archive and clear the live data so you can start fresh.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm text-amber-800">
              <p className="font-semibold mb-1">What happens:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>All current events are saved to an archive snapshot</li>
                <li>Live analytics data is cleared</li>
                <li>Archived data remains viewable and exportable from the Archives tab</li>
                <li>New events will start accumulating fresh</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setArchiveConfirm(false)}
                disabled={archiving}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setArchiving(true);
                  const result = await archiveAnalytics();
                  if (result.success) {
                    setActionMessage({ type: 'success', text: `Archived ${result.count} events successfully.` });
                    await loadData();
                  } else {
                    setActionMessage({ type: 'error', text: result.error || 'Failed to archive.' });
                  }
                  setArchiving(false);
                  setArchiveConfirm(false);
                  setTimeout(() => setActionMessage(null), 5000);
                }}
                disabled={archiving}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl font-bold hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {archiving ? (
                  <><RefreshCw size={18} className="animate-spin" /> Archiving...</>
                ) : (
                  <><Archive size={18} /> Archive & Clear</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Data Confirmation Modal */}
      {clearConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <Trash2 className="text-red-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Clear Analytics Data</h3>
            </div>

            <p className="text-gray-600 mb-2">
              This will permanently delete all <span className="font-bold">{analyticsEvents.length} events</span> without archiving. This cannot be undone.
            </p>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-800">
              <p className="font-semibold mb-1">Warning:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>All live analytics data will be permanently deleted</li>
                <li>No archive snapshot will be created</li>
                <li>This action cannot be reversed</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setClearConfirm(false)}
                disabled={archiving}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setArchiving(true);
                  const result = await clearAnalytics();
                  if (result.success) {
                    setActionMessage({ type: 'success', text: `Cleared ${result.count} events permanently.` });
                    await loadData();
                  } else {
                    setActionMessage({ type: 'error', text: result.error || 'Failed to clear.' });
                  }
                  setArchiving(false);
                  setClearConfirm(false);
                  setTimeout(() => setActionMessage(null), 5000);
                }}
                disabled={archiving}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-xl font-bold hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {archiving ? (
                  <><RefreshCw size={18} className="animate-spin" /> Clearing...</>
                ) : (
                  <><Trash2 size={18} /> Clear Permanently</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="text-red-600" size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete User</h3>
            </div>

            <p className="text-gray-600 mb-2">
              Are you sure you want to permanently delete this user?
            </p>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <div className="font-bold text-gray-900">{deleteConfirm.username || '—'}</div>
              <div className="text-sm text-gray-500">{deleteConfirm.email}</div>
              <div className="text-xs text-gray-400 mt-1">
                {deleteConfirm.subscription === 'paid' ? `Paid (${deleteConfirm.planType || 'active'})` : 'Free'} • Phase {deleteConfirm.currentPhase || 1} • Joined {formatDate(deleteConfirm.createdAt)}
              </div>
            </div>

            <p className="text-xs text-red-600 font-semibold mb-6">
              This will permanently delete their account, all progress, messages, friend connections, Stripe subscription, and analytics data. This cannot be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Trash2 size={18} />
                {deleteLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
